import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { languageCodes, defaultLanguage } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';

export default function Footer() {
  const { isDarkish: isDark } = useTheme();
  const { t, i18n } = useTranslation();

  const getLocalizedPath = (path) => {
    if (i18n.language === defaultLanguage) return path;
    return `/${i18n.language}${path === '/' ? '' : path}`;
  };

  const footerLinks = {
    [t('footer.resources', 'Platform')]: [
      { name: t('brokers.title', 'Broker Directory'), path: '/brokers' },
      { name: t('charts.title', 'Live Charts'), path: '/charts' },
      { name: t('calendar.title', 'Economic Calendar'), path: '/calendar' },
      { name: t('news.title', 'Market News'), path: '/news' },
      { name: t('toolbox.title', 'Trading Tools'), path: '/toolbox' },
      { name: t('traders.title', 'Top Traders'), path: '/traders' },
    ],
    [t('footer.support', 'Services')]: [
      { name: t('setup_broker.title', 'Setup Your Own FX Broker'), path: '/setup-broker', highlight: true },
      { name: t('tournament.title', 'Trading Tournament'), path: '/tournament', hot: true },
      { name: 'Broker Listing', path: '/register' },
      { name: 'Advertise With Us', path: '/contact' },
    ],
    [t('footer.company', 'Company')]: [
      { name: t('footer.about', 'About Us'), path: '/about' },
      { name: t('footer.contact', 'Contact'), path: '/contact' },
      { name: t('footer.careers', 'Careers'), path: '/careers' },
      { name: t('footer.press', 'Press'), path: '/press' },
    ],
    [t('footer.legal', 'Legal')]: [
      { name: t('footer.privacy', 'Privacy Policy'), path: '/legal/privacy' },
      { name: t('footer.terms', 'Terms of Service'), path: '/legal/terms' },
      { name: t('footer.disclaimer', 'Risk Disclosure'), path: '/legal/risk' },
      { name: t('footer.cookies', 'Cookie Policy'), path: '/legal/cookies' },
    ],
  };

  return (
    <footer
      data-testid="footer"
      className={cn(
        "border-t",
        isDark ? "bg-[#0a0a0f] border-white/[0.05]" : "bg-gray-50 border-gray-200/60"
      )}
    >
      {/* Main Footer */}
      <div className="container-app py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to={getLocalizedPath("/")} className="inline-flex items-center gap-2 mb-3">
              <img
                src={isDark ? "/logo-white.png" : "/logo-color.png"}
                alt="FX Astra"
                className="h-7 w-auto"
              />
            </Link>
            <p className={cn("text-xs leading-relaxed max-w-[240px]", isDark ? "text-gray-500" : "text-gray-600")}>
              {t('about.subtitle', 'The global forex broker comparison platform trusted by millions of traders worldwide.')}
            </p>

            <div className="mt-5">
              <LanguageSwitcher variant="prominent" />
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4
                className={cn(
                  "text-[10px] font-semibold uppercase tracking-[0.14em] mb-3",
                  isDark ? "text-gray-500" : "text-gray-500"
                )}
              >
                {category}
              </h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={getLocalizedPath(link.path)}
                      data-testid={link.highlight ? 'setup-broker-footer-link' : undefined}
                      className={cn(
                        "text-sm transition-colors inline-flex items-center gap-2",
                        link.highlight
                          ? isDark ? "text-orange-400 hover:text-orange-300" : "text-orange-600 hover:text-orange-500"
                          : link.hot
                          ? isDark ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-500"
                          : isDark ? "text-gray-400 hover:text-white" : "text-gray-700 hover:text-gray-900"
                      )}
                    >
                      {link.name}
                      {link.hot && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-red-500/15 text-red-400 rounded">
                          Hot
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={cn("border-t", isDark ? "border-white/[0.04]" : "border-gray-200/60")}>
        <div className="container-app py-5">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className={cn("text-xs", isDark ? "text-gray-500" : "text-gray-600")}>
              {t('footer.copyright', '© 2026 FX Astra. All rights reserved.')}
            </p>
            <p className={cn("text-[11px] max-w-2xl md:text-right leading-relaxed", isDark ? "text-gray-600" : "text-gray-500")}>
              <span className={cn("font-semibold", isDark ? "text-gray-400" : "text-gray-600")}>
                {t('footer.disclaimer', 'Risk Warning')}:
              </span>{' '}
              Trading forex and CFDs carries significant risk of loss. Past performance is not indicative of future results. Only trade with capital you can afford to lose.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
